import { Topbar } from "../components/Topbar";
import { KpiCard } from "../components/KpiCard";
import { Panel } from "../components/Panel";
import { DataTable } from "../components/DataTable";
import { LoadingState, ErrorState } from "../components/States";
import { useDashboard } from "../hooks/useDashboard";
import { formatHours, formatMonthLabel, formatNumber, formatPct } from "../lib/dates";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, PieChart, Pie, Cell, Legend,
} from "recharts";

const COLORS = ["#002FA7", "#94A3B8"];

export default function Timesheets() {
  const { data, loading, error } = useDashboard("timesheets");

  return (
    <>
      <Topbar title="Partes de horas" subtitle="Dedicación por empleado y proyecto" />
      <div className="p-6 space-y-6" data-testid="timesheets-page">
        {loading && <LoadingState />}
        {error && <ErrorState message={error} />}
        {data && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
              <KpiCard testId="kpi-ts-hours" label="Horas totales" value={formatHours(data.kpis.total_hours)} />
              <KpiCard testId="kpi-ts-entries" label="Entradas" value={formatNumber(data.kpis.total_entries, { maximumFractionDigits: 0 })} />
              <KpiCard testId="kpi-ts-billable" label="Facturables" value={formatHours(data.kpis.billable_hours)} accent="success" />
              <KpiCard testId="kpi-ts-nonbillable" label="No facturables" value={formatHours(data.kpis.non_billable_hours)} />
              <KpiCard testId="kpi-ts-pct" label="% Facturable" value={formatPct(data.kpis.billable_pct)} accent={data.kpis.billable_pct >= 50 ? "success" : "warning"} />
              <KpiCard testId="kpi-ts-employees" label="Empleados" value={formatNumber(data.kpis.active_employees, { maximumFractionDigits: 0 })} />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <Panel title="Evolución mensual" subtitle="Facturables vs no facturables" className="lg:col-span-2" testId="chart-ts-trend">
                <div className="h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={data.monthly_trend}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                      <XAxis dataKey="month" tickFormatter={formatMonthLabel} tick={{ fontSize: 11, fill: "#64748B" }} />
                      <YAxis tick={{ fontSize: 11, fill: "#64748B" }} />
                      <Tooltip formatter={(v) => formatHours(v)} labelFormatter={formatMonthLabel} />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                      <Bar dataKey="facturable" name="Facturables" stackId="a" fill="#002FA7" />
                      <Bar dataKey="no_facturable" name="No facturables" stackId="a" fill="#94A3B8" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </Panel>

              <Panel title="Reparto facturabilidad" testId="chart-ts-billable">
                <div className="h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={data.billable_split} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={85} innerRadius={45}>
                        {data.billable_split.map((_, i) => (
                          <Cell key={i} fill={COLORS[i % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(v) => formatHours(v)} />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </Panel>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <Panel title="Horas por empleado" subtitle="Top 15" testId="table-hours-employee">
                <DataTable
                  testId="employees-table"
                  filename="horas_por_empleado.csv"
                  rows={data.per_employee}
                  columns={[
                    { key: "employee", label: "Empleado", accessor: "employee" },
                    { key: "entries", label: "Entradas", accessor: "entries" },
                    {
                      key: "hours", label: "Horas", accessor: "hours",
                      render: (r) => <span className="tabular-nums font-medium">{formatHours(r.hours)}</span>,
                    },
                  ]}
                />
              </Panel>

              <Panel title="Horas por proyecto" subtitle="Top 15" testId="table-hours-project">
                <DataTable
                  testId="ts-projects-table"
                  filename="horas_por_proyecto.csv"
                  rows={data.per_project}
                  columns={[
                    { key: "project", label: "Proyecto", accessor: "project" },
                    { key: "entries", label: "Entradas", accessor: "entries" },
                    {
                      key: "hours", label: "Horas", accessor: "hours",
                      render: (r) => <span className="tabular-nums font-medium">{formatHours(r.hours)}</span>,
                    },
                  ]}
                />
              </Panel>
            </div>
          </>
        )}
      </div>
    </>
  );
}
